import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Paper,
  Divider,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { BatchProgress, UploadPhase } from 'src/hooks/useFileUpload';

interface UploadedFile {
  id: string;
  file: File;
  status: 'waiting' | 'uploading' | 'completed' | 'failed';
  error: string | null;
}

interface UploadStatusDialogProps {
  open: boolean;
  onClose: () => void;
  onDismiss: () => void;
  uploadQueue: UploadedFile[];
  batchProgress: BatchProgress;
}

function phaseLabel(phase: UploadPhase, uploaded: number, total: number, failed: number): string {
  switch (phase) {
    case 'preparing':
      return 'Preparing upload…';
    case 'uploading':
      return `Uploading ${uploaded} of ${total} file${total !== 1 ? 's' : ''}…`;
    case 'finalizing':
      return `Queuing ${uploaded} file${uploaded !== 1 ? 's' : ''} for inference…`;
    case 'done':
      if (failed > 0 && uploaded === 0) return `All ${total} uploads failed.`;
      if (failed > 0) return `${uploaded} uploaded, ${failed} failed.`;
      return `${uploaded} file${uploaded !== 1 ? 's' : ''} uploaded successfully.`;
    default:
      return '';
  }
}

function FileStatusIcon({ status }: { status: UploadedFile['status'] }) {
  switch (status) {
    case 'completed':
      return <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />;
    case 'failed':
      return <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />;
    case 'uploading':
      return <UploadFileIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    default:
      return <HourglassEmptyIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
  }
}

const UploadStatusDialog: FC<UploadStatusDialogProps> = ({
  open,
  onClose,
  onDismiss,
  uploadQueue,
  batchProgress,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    if (open) setIsMinimized(false);
  }, [open]);

  if (!open || uploadQueue.length === 0) return null;

  const { phase, uploadedCount, totalCount, failedCount, overallPct } = batchProgress;
  const isDone = phase === 'done';
  const isIndeterminate = phase === 'preparing' || phase === 'finalizing';

  const failed = uploadQueue.filter((f) => f.status === 'failed');
  const rest = uploadQueue.filter((f) => f.status !== 'failed').slice(-15);
  const displayQueue = [...failed, ...rest];

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 0,
        right: 24,
        width: { xs: '100%', sm: 380 },
        maxHeight: isMinimized ? 56 : 440,
        overflow: 'hidden',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        transition: 'max-height 0.25s ease',
        zIndex: 1400,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          bgcolor: 'background.paper',
          minHeight: 56,
          flexShrink: 0,
        }}
        onClick={() => setIsMinimized((v) => !v)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {isDone ? 'Upload complete' : 'Uploading files'}
          </Typography>
          {totalCount > 0 && (
            <Chip
              label={`${uploadedCount + failedCount} / ${totalCount}`}
              size="small"
              color={isDone && failedCount === 0 ? 'success' : isDone && failedCount > 0 ? 'warning' : 'default'}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {isDone && (
            <Button
              size="small"
              color="inherit"
              onClick={(e) => { e.stopPropagation(); onDismiss(); }}
              sx={{ mr: 0.5, fontSize: 12 }}
            >
              Dismiss
            </Button>
          )}
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setIsMinimized(true); }}
            aria-label="Minimize upload progress"
          >
            <MinimizeIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            aria-label="Close upload progress"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {!isMinimized && (
        <>
          <Box sx={{ px: 2, pb: 1, bgcolor: 'background.paper', flexShrink: 0 }}>
            <LinearProgress
              variant={isIndeterminate ? 'indeterminate' : 'determinate'}
              value={isIndeterminate ? undefined : overallPct}
              sx={{ height: 6, borderRadius: 3, mb: 0.75 }}
              color={isDone && failedCount > 0 ? 'warning' : isDone ? 'success' : 'primary'}
            />
            <Typography variant="caption" color="text.secondary">
              {phaseLabel(phase, uploadedCount, totalCount, failedCount)}
            </Typography>
          </Box>

          <Divider />

          <List dense disablePadding sx={{ overflowY: 'auto', flexGrow: 1, py: 0.5 }}>
            {displayQueue.length === 0 ? (
              <ListItem>
                <ListItemText secondary="No files in queue." />
              </ListItem>
            ) : (
              displayQueue.map((f) => (
                <ListItem key={f.id} sx={{ py: 0.25, px: 2 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <FileStatusIcon status={f.status} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" noWrap title={f.file.name}>
                        {f.file.name}
                      </Typography>
                    }
                    secondary={
                      f.status === 'failed' && f.error ? (
                        <Typography variant="caption" color="error.main" noWrap>
                          {f.error}
                        </Typography>
                      ) : f.status === 'uploading' ? (
                        <Typography variant="caption" color="primary.main">
                          Uploading…
                        </Typography>
                      ) : f.status === 'waiting' ? (
                        <Typography variant="caption" color="text.disabled">
                          Waiting…
                        </Typography>
                      ) : null
                    }
                  />
                </ListItem>
              ))
            )}
            {uploadQueue.length > displayQueue.length && (
              <ListItem sx={{ py: 0.25, px: 2 }}>
                <ListItemText
                  secondary={
                    <Typography variant="caption" color="text.disabled">
                      + {uploadQueue.length - displayQueue.length} more files
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </>
      )}
    </Paper>
  );
};

export default UploadStatusDialog;

